"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useScroll, useSpring, useTransform, motion } from "motion/react";
import { FC, PropsWithChildren, useRef, useState, useEffect } from "react";
import { Button } from "./button";
import { cn } from "../lib/utils";

export const HorizontallyScrollable: FC<
  PropsWithChildren<{ shadowClassName?: string }>
> = ({ shadowClassName, children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasScrollArea, setHasScrollArea] = useState(false);
  const { scrollXProgress } = useScroll({ container: containerRef });
  const leftGradientOpacity = useSpring(
    useTransform(scrollXProgress, [0, 0.3], [0, 1]),
  );
  const rightGradientOpacity = useSpring(
    useTransform(scrollXProgress, [0, 0.6, 1], [1, 1, 0]),
  );

  const checkHasScroll = () => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const hasScroll = el.scrollWidth > el.clientWidth;
    setHasScrollArea(hasScroll);
  };

  useEffect(() => {
    checkHasScroll();

    const el = containerRef.current;
    if (!el) return;

    window.addEventListener("resize", checkHasScroll);

    const resizeObserver = new ResizeObserver(checkHasScroll);
    resizeObserver.observe(el);

    return () => {
      window.removeEventListener("resize", checkHasScroll);
      resizeObserver.disconnect();
    };
  }, []);

  const scrollTo = (direction: "left" | "right", distance: number) => {
    containerRef.current?.scrollBy({
      left: direction === "right" ? distance : -distance,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative flex items-center max-w-full overflow-hidden gap-8">
      <div
        className="flex max-w-full overflow-hidden relative"
        ref={containerRef}
      >
        {children}
      </div>

      {hasScrollArea && (
        <>
          <motion.span
            style={{
              opacity: leftGradientOpacity,
            }}
            className={cn(
              "absolute top-0 left-[-1px] h-full w-1/12 pointer-events-none bg-[image:linear-gradient(90deg,rgba(255,254,242,0)_0%,#FFFEF2_100%)] rotate-180",
              shadowClassName,
            )}
          />
          <motion.span
            style={{
              opacity: rightGradientOpacity,
            }}
            className={cn(
              "absolute top-0 right-[104px] h-full w-1/12 pointer-events-none bg-[image:linear-gradient(90deg,rgba(255,254,242,0)_0%,#FFFEF2_100%)]",
              shadowClassName,
            )}
          />
          <div className="z-20 h-full flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-transparent p-2 h-8 w-8 rounded-md"
              onClick={() => scrollTo("left", 200)}
            >
              <ChevronLeft size={16} className="text-gray-600" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-[#EAEAEA] p-2 h-8 w-8 rounded-md"
              onClick={() => scrollTo("right", 200)}
            >
              <ChevronRight size={16} className="text-gray-600" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
